import React, {useEffect, useRef, useState} from 'react';
import {Autocomplete} from '@material-ui/lab';
import {mapMedicinalProductForAutocomplete} from '../../../utils/prescription';
import {CircularProgress, TextField, useTheme} from '@material-ui/core';
import {MedicinalProductDto} from '../../../types/prescription';
import prescriptionsApi from '../../../api/prescriptions/prescriptionsApi';
import {debounce} from 'lodash-es';
import {useTranslation} from 'react-i18next';
import i18nNamespaces from '../../../const/i18nNamespaces';
import { referralApi } from '../../../api/referral/referralApi';
import { getTranslation } from '../../../utils/translation';

export type Icd9Code = {
    code: string;
    namePl: string;
    nameEn: string;
}

const DEBOUNCE_TIME = 1000;
interface Icd9CodeAutocompleteProps {
    label: string;
    onChange: (newValue: string | Icd9Code) => void;
    icd9Code: Icd9Code | null;
    error?: boolean;
    helperText?: React.ReactNode;
}

const Icd9CodeAutocomplete = (
    props: Icd9CodeAutocompleteProps,
) => {
    const {label, onChange, icd9Code, error, helperText} = props;
    const [Icd9CodeInputValue, setIcd9CodeInputValue] =
        useState<string>('');
    const [icd9CodeLoading, setIcd9CodeLoading] = useState<boolean>(false);
    const [isTyping, setIsTyping] = useState<boolean>(false);
    const [icd9Codes, setIcd9Codes] = useState<
        Icd9Code[]
    >([]);
    const icd9CodeInputRef = useRef(null);
    const {t, i18n} = useTranslation(i18nNamespaces.REFERRAL);
    const theme = useTheme();

    const fetchIcd9CodesList = async (searchValue: string) => {
        try {
            const {data} = await referralApi.getIcd9Codes(
                searchValue,
            );
            if(data.isSuccess)
                setIcd9Codes(data.result);
        } catch (error) {
            console.error(error);
        } finally {
            setIcd9CodeLoading(false);
        }
    };

    const debouncedSearch = useRef(
        debounce(async (search: string) => {
            try {
                setIcd9CodeLoading(true);
                setIsTyping(false);
                await fetchIcd9CodesList(search);
            } finally {
                setIcd9CodeLoading(false);
            }
        }, DEBOUNCE_TIME),
    ).current;

    useEffect(() => {
        debouncedSearch(Icd9CodeInputValue);
    }, [Icd9CodeInputValue]);

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    const handleChangeMedicalProductInput = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setIcd9CodeInputValue(event.target.value);
        setIsTyping(true);
        debouncedSearch(event.target.value);
    };

    const renderOptionLabel = (option: Icd9Code) => {
        if(!option) return null;
        const name = i18n.language === 'en' ? option.nameEn : option.namePl;
        return `${option.code} - ${name}`;
    }

    return (
        <Autocomplete
            loading={icd9CodeLoading}
            options={icd9Codes}
            getOptionSelected={option => option.code === icd9Code?.code}
            getOptionLabel={renderOptionLabel}
            noOptionsText={t('noResults')}
            value={icd9Code}
            onChange={(_, newValue) => onChange(newValue)}
            renderInput={params => (
                <TextField
                    {...params}
                    inputRef={icd9CodeInputRef}
                    onInput={handleChangeMedicalProductInput}
                    label={label}
                    variant="outlined"
                    error={error}
                    helperText={helperText}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {(isTyping || icd9CodeLoading) ? (
                                    <CircularProgress
                                        color="secondary"
                                        size={20}
                                        style={{marginRight: 10}}
                                    />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
        />
    );
}

export default Icd9CodeAutocomplete;
